import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CurrencyInput } from '@/components/ui/currency-input';
import { SubmitDealStepProps } from '@/types/submit-deal.types';
import { useLanguage } from '@/contexts/LanguageContext';

export const StepOperations = ({ formData, updateFormData, errors, clearError, t }: SubmitDealStepProps) => {
  const { language } = useLanguage();
  const isFr = language === 'fr';

  const handleChange = (field: string, value: string | number | boolean) => {
    updateFormData('operations', { [field]: value });
    clearError(field);
  };

  const handleNumericChange = (field: string, rawValue: string) => {
    const value = rawValue === '' ? '' : parseInt(rawValue);
    updateFormData('operations', { [field]: value });
    clearError(field);
  };

  const updateImpact = (field: string, value: number | boolean) => {
    updateFormData('impact', { [field]: value });
  };

  const impactNumber = (field: string, rawValue: string, decimals = false) => {
    const v = rawValue === '' ? 0 : decimals ? parseFloat(rawValue) : parseInt(rawValue);
    updateFormData('impact', { [field]: isNaN(v as number) ? 0 : v });
  };


  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="fullTimeEmployees">{t.step2.fullTimeEmployees} *</Label>
          <Input
            id="fullTimeEmployees"
            type="number"
            min={0}
            value={formData.operations.fullTimeEmployees || ''}
            onChange={(e) => handleNumericChange('fullTimeEmployees', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="partTimeEmployees">{t.step2.partTimeEmployees} *</Label>
          <Input
            id="partTimeEmployees"
            type="number"
            min={0}
            value={formData.operations.partTimeEmployees || ''}
            onChange={(e) => handleNumericChange('partTimeEmployees', e.target.value)}
          />
        </div>

        {/* AJOUTÉ : Sous-traitants — confirmé absent du formulaire */}
        <div className="space-y-2">
          <Label htmlFor="subcontractors">{isFr ? 'Sous-traitants (nombre typique)' : 'Subcontractors (typical count)'}</Label>
          <Input
            id="subcontractors"
            type="number"
            min={0}
            value={formData.operations.subcontractors || ''}
            onChange={(e) => handleNumericChange('subcontractors', e.target.value)}
          />
        </div>

        {/* AJOUTÉ : Langues — confirmé absent du formulaire */}
        <div className="space-y-2">
          <Label htmlFor="languages">{isFr ? 'Langues de travail' : 'Working Languages'}</Label>
          <Input
            id="languages"
            value={formData.operations.languages || ''}
            onChange={(e) => handleChange('languages', e.target.value)}
            placeholder={isFr ? 'Français, Anglais...' : 'French, English...'}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="dailyOperationsDescription">{t.step2.dailyOperationsDescription}</Label>
        <Textarea
          id="dailyOperationsDescription"
          value={formData.operations.dailyOperationsDescription}
          onChange={(e) => handleChange('dailyOperationsDescription', e.target.value)}
          placeholder={t.step2.dailyOperationsPlaceholder}
          rows={4}
        />
      </div>

      <div className="space-y-2">
        <Label>{t.step2.processDocumentationLevel}</Label>
        <Select
          value={formData.operations.processDocumentationLevel.toString()}
          onValueChange={(value) => handleChange('processDocumentationLevel', parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[1, 2, 3, 4, 5].map((level) => (
              <SelectItem key={level} value={level.toString()}>
                {t.step2.documentationLevels[level as 1 | 2 | 3 | 4 | 5]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customerConcentration">{t.step2.customerConcentration}</Label>
          <Input
            id="customerConcentration"
            type="number"
            min={0}
            max={100}
            value={formData.operations.customerConcentration || ''}
            onChange={(e) => handleNumericChange('customerConcentration', e.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t.step2.customerConcentrationHint}</p>
        </div>

        {/* AJOUTÉ : Fournisseurs clés — confirmé absent du formulaire */}
        <div className="space-y-2">
          <Label htmlFor="keySuppliers">{isFr ? 'Fournisseurs clés' : 'Key Suppliers'}</Label>
          <Textarea
            id="keySuppliers"
            value={formData.operations.keySuppliers || ''}
            onChange={(e) => handleChange('keySuppliers', e.target.value)}
            placeholder={isFr ? 'Nommez les principaux fournisseurs et leur importance relative' : 'Name key suppliers and their relative importance'}
            rows={2}
          />
        </div>
      </div>

      {/* AJOUTÉ : Affiliation syndicale — le type le supportait déjà mais
          aucune question ne la collectait dans le formulaire. */}
      <div className="border-t pt-6">
        <h4 className="text-lg font-medium mb-4">{isFr ? 'Relations de travail' : 'Labour Relations'}</h4>
        <div className="space-y-3">
          <Label>{isFr ? "Le personnel est-il syndiqué?" : 'Is staff unionized?'}</Label>
          <RadioGroup
            value={formData.operations.unionAffiliated ? 'yes' : 'no'}
            onValueChange={(value) => handleChange('unionAffiliated', value === 'yes')}
            className="flex space-x-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="unionAffiliated-yes" />
              <Label htmlFor="unionAffiliated-yes" className="font-normal">{isFr ? 'Oui' : 'Yes'}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="unionAffiliated-no" />
              <Label htmlFor="unionAffiliated-no" className="font-normal">{isFr ? 'Non' : 'No'}</Label>
            </div>
          </RadioGroup>
          {formData.operations.unionAffiliated && (
            <div className="space-y-2 mt-2">
              <Label htmlFor="unionDetails">{isFr ? 'Détails (syndicat, convention collective)' : 'Details (union, collective agreement)'}</Label>
              <Input
                id="unionDetails"
                value={formData.operations.unionDetails || ''}
                onChange={(e) => handleChange('unionDetails', e.target.value)}
                placeholder={isFr ? 'ex. CCQ — tous les corps de métier' : 'e.g. CCQ — all trades'}
              />
            </div>
          )}
        </div>
      </div>

      {/* AJOUTÉ : Taux de roulement — le type le supportait déjà
          (employeeTurnoverRate) mais aucune question ne le collectait. */}
      <div className="space-y-2">
        <Label htmlFor="employeeTurnoverRate">{isFr ? 'Taux de roulement annuel du personnel (%)' : 'Annual Employee Turnover Rate (%)'}</Label>
        <Input
          id="employeeTurnoverRate"
          type="number"
          min={0}
          max={100}
          value={formData.operations.employeeTurnoverRate || ''}
          onChange={(e) => handleNumericChange('employeeTurnoverRate', e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          {isFr ? "% des employés qui quittent l'entreprise chaque année" : '% of employees who leave the company each year'}
        </p>
      </div>

      <div className="border-t pt-6">
        <h4 className="text-lg font-medium mb-4">{t.step2.ownerInvolvement}</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ownerHoursPerWeek">{t.step2.ownerHoursPerWeek} *</Label>
            <Input
              id="ownerHoursPerWeek"
              type="number"
              min={0}
              max={168}
              value={formData.operations.ownerHoursPerWeek || ''}
              onChange={(e) => handleNumericChange('ownerHoursPerWeek', e.target.value)}
              className={errors.ownerHoursPerWeek ? 'border-destructive' : ''}
            />
            {errors.ownerHoursPerWeek && <p className="text-sm text-destructive">{errors.ownerHoursPerWeek}</p>}
          </div>

          <div className="space-y-2">
            <Label>{t.step2.ownerType} *</Label>
            <RadioGroup
              value={formData.operations.ownerType}
              onValueChange={(value) => handleChange('ownerType', value)}
              className="flex flex-col space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="active" id="active" />
                <Label htmlFor="active" className="font-normal">{t.step2.ownerTypes.active}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="semiActive" id="semiActive" />
                <Label htmlFor="semiActive" className="font-normal">{t.step2.ownerTypes.semiActive}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="absentee" id="absentee" />
                <Label htmlFor="absentee" className="font-normal">{t.step2.ownerTypes.absentee}</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        {/* AJOUTÉ : Salaire annuel du propriétaire — écrit par l'analyseur
            de documents IA mais jamais collecté manuellement jusqu'ici. */}
        <div className="space-y-2 mt-4">
          <Label htmlFor="ownerAnnualSalary">{isFr ? 'Salaire annuel du propriétaire' : "Owner's Annual Salary"}</Label>
          <CurrencyInput
            id="ownerAnnualSalary"
            min={0}
            value={formData.operations.ownerAnnualSalary || ''}
            onChange={(v) => handleChange('ownerAnnualSalary', v === '' ? 0 : parseFloat(v))}
          />
          <p className="text-xs text-muted-foreground">
            {isFr
              ? 'Utilisé pour calculer le SDE (Seller Discretionary Earnings) et le DSCR.'
              : 'Used to calculate SDE (Seller Discretionary Earnings) and DSCR.'}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ownerCriticalTasks">{t.step2.ownerCriticalTasks}</Label>
        <Textarea
          id="ownerCriticalTasks"
          value={formData.operations.ownerCriticalTasks}
          onChange={(e) => handleChange('ownerCriticalTasks', e.target.value)}
          placeholder={t.step2.ownerCriticalTasksPlaceholder}
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="ownerAbsenceImpact">{t.step2.ownerAbsenceImpact}</Label>
        <Textarea
          id="ownerAbsenceImpact"
          value={formData.operations.ownerAbsenceImpact}
          onChange={(e) => handleChange('ownerAbsenceImpact', e.target.value)}
          placeholder={t.step2.ownerAbsenceImpactPlaceholder}
          rows={3}
        />
      </div>

      {/* Impact, Workforce & Sustainability */}
      <div className="border-t pt-6 space-y-6">
        <h4 className="text-lg font-medium">
          {isFr ? 'Impact, main-d\u2019\u0153uvre et durabilit\u00e9' : 'Impact, Workforce & Sustainability'}
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="employeeCount">
              {isFr ? 'Nombre d\u2019employ\u00e9s' : 'Number of employees'} *
            </Label>
            <Input
              id="employeeCount"
              type="number"
              min={1}
              value={formData.impact.employeeCount || ''}
              onChange={(e) => impactNumber('employeeCount', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="averageEmployeeTenureYears">
              {isFr ? 'Anciennet\u00e9 moyenne des employ\u00e9s (ann\u00e9es)' : 'Average employee tenure (years)'}
            </Label>
            <Input
              id="averageEmployeeTenureYears"
              type="number"
              min={0}
              step={0.5}
              value={formData.impact.averageEmployeeTenureYears || ''}
              onChange={(e) => impactNumber('averageEmployeeTenureYears', e.target.value, true)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="workplaceAccidentsLast3yr">
              {isFr ? 'Accidents de travail lors des 3 derni\u00e8res ann\u00e9es' : 'Workplace accidents in last 3 years'}
            </Label>
            <Input
              id="workplaceAccidentsLast3yr"
              type="number"
              min={0}
              value={formData.impact.workplaceAccidentsLast3yr ?? 0}
              onChange={(e) => impactNumber('workplaceAccidentsLast3yr', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="localSupplyChainPercent">
              {isFr ? '% de fournisseurs dans un rayon de 200km' : '% of suppliers within 200km'}
            </Label>
            <Input
              id="localSupplyChainPercent"
              type="number"
              min={0}
              max={100}
              value={formData.impact.localSupplyChainPercent ?? 0}
              onChange={(e) => impactNumber('localSupplyChainPercent', e.target.value)}
            />
          </div>
        </div>

        {[
          {
            field: 'hasRetirementPlan',
            label: isFr
              ? 'L\u2019entreprise offre-t-elle un r\u00e9gime de retraite ou de participation aux b\u00e9n\u00e9fices?'
              : 'Does the business offer a retirement or profit-sharing plan?',
          },
          {
            field: 'hasEnvironmentalCertification',
            label: isFr
              ? 'L\u2019entreprise d\u00e9tient-elle des certifications environnementales? (ISO 14001, B Corp, etc.)'
              : 'Does the business hold any environmental certifications? (ISO 14001, B Corp, Green Business, etc.)',
          },
          {
            field: 'hasLitigationHistory',
            label: isFr
              ? 'Des litiges juridiques au cours des 5 derni\u00e8res ann\u00e9es?'
              : 'Any litigation or legal disputes in the last 5 years?',
          },
          {
            field: 'communityInvolvement',
            label: isFr
              ? 'Implication communautaire ou philanthropique active?'
              : 'Active community or philanthropic involvement?',
          },
        ].map(({ field, label }) => (
          <div key={field} className="space-y-2">
            <Label>{label}</Label>
            <RadioGroup
              value={(formData.impact as any)[field] ? 'yes' : 'no'}
              onValueChange={(v) => updateImpact(field, v === 'yes')}
              className="flex space-x-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id={`${field}-yes`} />
                <Label htmlFor={`${field}-yes`} className="font-normal">{isFr ? 'Oui' : 'Yes'}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id={`${field}-no`} />
                <Label htmlFor={`${field}-no`} className="font-normal">{isFr ? 'Non' : 'No'}</Label>
              </div>
            </RadioGroup>
          </div>
        ))}
      </div>
    </div>
  );
};